import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { dateTimeFormatter } from "../../../util/dateFormatter";
import type { SessionInfoProps } from "./types";

type EditableInfoField = "duration" | "timeRange" | "location";

const toValidDate = (value?: Date | string) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const toLocalTimeInput = (value?: Date | string) => {
  const parsed = toValidDate(value);
  if (!parsed) return "";
  const hours = parsed.getHours().toString().padStart(2, "0");
  const minutes = parsed.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const parseTimeInput = (value: string) => {
  const [hoursRaw, minutesRaw] = value.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return undefined;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return undefined;
  return { hours, minutes };
};

const SessionInfoSection: React.FC<SessionInfoProps> = ({
  session,
  sessionRemarks,
  onSessionRemarksChange,
  onSessionInfoUpdate,
  onSave,
  readOnly,
}) => {
  const { t } = useTranslation();
  const [editingSessionName, setEditingSessionName] = useState(false);
  const [editingField, setEditingField] = useState<EditableInfoField | null>(
    null,
  );
  const [nameDraft, setNameDraft] = useState("");
  const [durationDraft, setDurationDraft] = useState("");
  const [startTimeDraft, setStartTimeDraft] = useState("");
  const [endTimeDraft, setEndTimeDraft] = useState("");
  const [locationDraft, setLocationDraft] = useState("");

  const durationMinutes = useMemo(() => {
    const explicit = Number(session?.duration ?? 0);
    if (explicit > 0) return Math.round(explicit);
    const start = toValidDate(session?.startTime);
    const end = toValidDate(session?.endTime);
    if (!start || !end) return 0;
    return Math.max(5, Math.round((end.getTime() - start.getTime()) / 60000));
  }, [session?.duration, session?.startTime, session?.endTime]);

  const sessionDateValue = useMemo(() => {
    const start = toValidDate(session?.startTime);
    return start ? dateTimeFormatter.format(start) : "-";
  }, [session?.startTime]);

  const timeRangeValue = useMemo(() => {
    const start = toValidDate(session?.startTime);
    const end = toValidDate(session?.endTime);
    if (!start || !end) return "";
    const fmt = (d: Date) =>
      `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
    return `${fmt(start)}-${fmt(end)}`;
  }, [session?.startTime, session?.endTime]);

  useEffect(() => {
    if (!editingSessionName) {
      setNameDraft(session.name || "");
    }
    if (!editingField) {
      setDurationDraft(String(Math.max(5, durationMinutes || 5)));
      setStartTimeDraft(toLocalTimeInput(session.startTime));
      setEndTimeDraft(toLocalTimeInput(session.endTime));
      setLocationDraft(session.location || "");
    }
  }, [
    durationMinutes,
    editingField,
    editingSessionName,
    session.endTime,
    session.location,
    session.name,
    session.startTime,
  ]);

  const openSessionNameEditor = () => {
    setNameDraft(session.name || "");
    setEditingSessionName(true);
  };

  const openFieldEditor = (field: EditableInfoField) => {
    setDurationDraft(String(Math.max(5, durationMinutes || 5)));
    setStartTimeDraft(toLocalTimeInput(session.startTime));
    setEndTimeDraft(toLocalTimeInput(session.endTime));
    setLocationDraft(session.location || "");
    setEditingField(field);
  };

  const commitName = () => {
    const nextName = nameDraft.trim();
    if (!nextName || nextName === session.name) return;
    onSessionInfoUpdate({ name: nextName });
  };

  const commitDuration = () => {
    const parsed = Number(durationDraft);
    if (Number.isNaN(parsed) || !Number.isFinite(parsed)) return;
    const nextDuration = Math.max(5, Math.round(parsed));
    const updates: {
      duration?: number;
      endTime?: Date;
    } = { duration: nextDuration };
    const start = toValidDate(session.startTime);
    const end = toValidDate(session.endTime);
    if (start) {
      updates.endTime = new Date(start.getTime() + nextDuration * 60000);
    }
    const currentDuration = Number(session.duration ?? 0);
    const durationChanged = currentDuration !== nextDuration;
    const endChanged = updates.endTime
      ? !end || updates.endTime.getTime() !== end.getTime()
      : false;
    if (!durationChanged && !endChanged) return;
    onSessionInfoUpdate(updates);
  };

  const commitTimeRange = () => {
    const parsedStart = parseTimeInput(startTimeDraft);
    const parsedEnd = parseTimeInput(endTimeDraft);
    const currentStart = toValidDate(session.startTime);
    const currentEnd = toValidDate(session.endTime);
    if (!parsedStart || !parsedEnd || !currentStart || !currentEnd) return;

    const nextStart = new Date(currentStart);
    nextStart.setHours(parsedStart.hours, parsedStart.minutes, 0, 0);

    const nextEnd = new Date(currentEnd);
    nextEnd.setHours(parsedEnd.hours, parsedEnd.minutes, 0, 0);
    if (nextEnd.getTime() <= nextStart.getTime()) {
      nextEnd.setTime(nextStart.getTime() + 5 * 60000);
    }

    const nextDuration = Math.max(
      5,
      Math.round((nextEnd.getTime() - nextStart.getTime()) / 60000),
    );

    const unchanged =
      nextStart.getTime() === currentStart.getTime() &&
      nextEnd.getTime() === currentEnd.getTime() &&
      Number(session.duration ?? 0) === nextDuration;
    if (unchanged) return;

    onSessionInfoUpdate({
      startTime: nextStart,
      endTime: nextEnd,
      duration: nextDuration,
    });
  };

  const commitLocation = () => {
    const nextLocation = locationDraft.trim();
    const currentLocation = (session.location || "").trim();
    if (nextLocation === currentLocation) return;
    onSessionInfoUpdate({ location: nextLocation || undefined });
  };

  const confirmSessionName = () => {
    commitName();
    setEditingSessionName(false);
  };

  const confirmField = (field: EditableInfoField) => {
    if (field === "duration") {
      commitDuration();
    } else if (field === "timeRange") {
      commitTimeRange();
    } else if (field === "location") {
      commitLocation();
    }
    setEditingField(null);
  };

  const handleSessionNameKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      confirmSessionName();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setEditingSessionName(false);
    }
  };

  const handleFieldKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    field: EditableInfoField,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      confirmField(field);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setEditingField(null);
    }
  };

  const renderReadOnlyCard = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: "primary.50",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0, width: "100%" }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            lineHeight: 1.2
          }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mt: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  const renderEditableCard = (
    field: EditableInfoField,
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    editor: React.ReactNode,
  ) => {
    const isEditing = editingField === field;
    return (
      <Box
        key={field}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          p: 1.5,
          pr: 5,
          borderRadius: 2,
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          "&:hover .field-edit-action": {
            opacity: 1,
          },
        }}
      >
        <IconButton
          className="field-edit-action"
          size="small"
          onClick={() =>
            isEditing ? confirmField(field) : openFieldEditor(field)
          }
          sx={[{
            position: "absolute",
            top: 6,
            right: 6,
            transition: "opacity 0.2s ease-out",
            bgcolor: "background.paper"
          }, isEditing ? {
            opacity: 1
          } : {
            opacity: 0
          }]}
        >
          {isEditing ? (
            <CheckIcon fontSize="small" color="primary" />
          ) : (
            <EditOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: "primary.50",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              lineHeight: 1.2
            }}>
            {label}
          </Typography>
          {isEditing ? (
            <Box sx={{ mt: 0.5 }}>{editor}</Box>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
              {value}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        bgcolor: "#fff",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          width: "fit-content",
          gap: 0.5,
        }}
      >
        {editingSessionName ? (
          <TextField
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            onKeyDown={handleSessionNameKeyDown}
            sx={{
              width: "50vh",
            }}
          />
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              flex: 1,
              minWidth: 0,
              pr: 1,
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
            {session.name || "-"}
          </Typography>
        )}

        <IconButton
          className="session-name-edit-action"
          size="small"
          onClick={() =>
            editingSessionName ? confirmSessionName() : openSessionNameEditor()
          }
          sx={{
            fontSize: 24,
            flexShrink: 0,
            bgcolor: "background.paper",
          }}
        >
          {editingSessionName ? (
            <CheckIcon fontSize="small" color="primary" />
          ) : (
            <EditOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "1fr 1fr",
            alignContent: "center",
          }}
        >
          {renderReadOnlyCard(
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 22, color: "primary.main" }}
            />,
            t("coachingNavigator.sessionDetails.sessionDate"),
            sessionDateValue,
          )}

          {renderEditableCard(
            "duration",
            <TimerOutlinedIcon sx={{ fontSize: 24, color: "primary.main" }} />,
            t("coachingNavigator.sessionDetails.sessionDuration"),
            t("coachingNavigator.sessionDetails.durationMinutes", {
              count: durationMinutes,
            }),
            <TextField
              value={durationDraft}
              onChange={(e) => setDurationDraft(e.target.value)}
              size="small"
              type="number"
              fullWidth
              autoFocus
              onKeyDown={(event) => handleFieldKeyDown(event, "duration")} slotProps={{ htmlInput: { min: 5 } }}
            />,
          )}

          {renderEditableCard(
            "timeRange",
            <ScheduleOutlinedIcon
              sx={{
                fontSize: 24,
                color: "primary.main",
                alignItems: "center",
              }}
            />,
            t("coachingNavigator.sessionDetails.timeRange"),
            timeRangeValue || "-",
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                value={startTimeDraft}
                onChange={(e) => setStartTimeDraft(e.target.value)}
                size="small"
                type="time"
                onKeyDown={(event) => handleFieldKeyDown(event, "timeRange")}
                fullWidth
                autoFocus
              />
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                -
              </Typography>
              <TextField
                value={endTimeDraft}
                onChange={(e) => setEndTimeDraft(e.target.value)}
                size="small"
                type="time"
                onKeyDown={(event) => handleFieldKeyDown(event, "timeRange")}
                fullWidth
              />
            </Box>,
          )}

          {renderEditableCard(
            "location",
            <PlaceOutlinedIcon sx={{ fontSize: 24, color: "primary.main" }} />,
            t("coachingNavigator.location"),
            session.location || "-",
            <TextField
              value={locationDraft}
              onChange={(e) => setLocationDraft(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              onKeyDown={(event) => handleFieldKeyDown(event, "location")}
            />,
          )}
        </Box>

        <Box>
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            {t("coachingNavigator.sessionDetails.sessionRemarks")}
          </Typography>
          <TextField
            value={sessionRemarks}
            onChange={(e) => onSessionRemarksChange(e.target.value)}
            onBlur={() => onSave?.()}
            placeholder={
              t("coachingNavigator.sessionDetails.sessionRemarksPlaceholder") ||
              ""
            }
            multiline
            rows={4}
            fullWidth
            sx={{
              mt: 1,
              "& .MuiInputBase-inputMultiline": {
                overflowY: "auto !important",
              },
            }}
            disabled={readOnly}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default SessionInfoSection;
